// 'user client'
import { memo, useContext, useMemo } from 'react'
import Link from 'components/Link'
import Image from 'next/image'

import { languageContext } from 'app/components/providers/languageProvider'
import { getValueById } from 'helpers/config/getValueById'
import { getCookie, setCookie } from 'helpers/cookies'
// import { chatSVG } from 'images/svg'
import ShowSalary from '../ShowSalary'
import { setJobViewCookiesTracker, viewJobSourceTracker } from 'components/ReportTracker/util'

import { useSharedData } from 'bossjob-remote/dist/hooks'

import styles from './jobDetail.pc.module.scss'

const Tags = ({ data }) => {
  if (!data?.length) {
    return null
  }

  return (
    <div className={styles.tabRoot}>
      {data?.map((v) => {
        return (
          <span className={styles.tabItem} key={v}>
            {v}
          </span>
        )
      })}
    </div>
  )
}

const useJobLocation = (remote_countries, is_remote_worldwide) => {
  const isWorldwide = is_remote_worldwide

  const { world_region_lists } = useSharedData('CONFIG') || {}
  const { companyDetail } = useContext(languageContext)

  const [country, countryMap, stateToCountryMap] = useMemo(() => {
    const covertCountry2Opt = (continent = []) => {
      let countryMap: Record<string, any> = {}
      let stateToCountryMap: Record<string, any[]> = {}
      const all = continent.map((v: any) => {
        let children: { label: any; value: any }[] = []
        if (v.countries) {
          let childIds: any[] = []

          children = v.countries.map((item) => {
            countryMap[item.id] = v.id
            childIds.push(item.id)

            return { value: item.id, label: item.value || item.display_name }
          })

          stateToCountryMap[v.id] = childIds
        }

        return { value: v.id, label: v.value || v.display_name, children }
      })
      return [all, countryMap, stateToCountryMap] as const
    }
    const country = world_region_lists
    return [...covertCountry2Opt(country)]
  }, [world_region_lists])

  const remoteValue = useMemo(() => {
    let remote_country_ids = remote_countries.map((v) => v.id)
    let newValue = {}

    const addToNewValue = (key, value) => {
      let arr = newValue[key] || []
      arr.push(value)

      newValue[key] = arr
    }

    let displayState = []
    let displayCountry = []

    if (
      !remote_country_ids?.length ||
      !Object.keys(countryMap).length ||
      !world_region_lists?.length
    ) {
      return null
    }

    remote_country_ids.forEach((v) => {
      addToNewValue(countryMap[v], v)
    })

    Object.keys(newValue).forEach((key) => {
      const state = world_region_lists.find((v) => Number(v.id) === Number(key))
      if (!state) {
        console.error(`world_region_lists has no this state, the state id is ${key}`)
        return
      }
      if (newValue[key].length === stateToCountryMap?.[key]?.length) {
        // all countries in this state have been found, so should display the state on page
        displayState.push(state)
      } else {
        let selectedCountries = newValue[key]
        ;(state.countries || []).forEach((item) => {
          if (selectedCountries.includes(item.id)) {
            displayCountry.push(item)
          }
        })
      }
    })

    return { displayState, displayCountry }
  }, [remote_countries, countryMap, stateToCountryMap, world_region_lists])

  const countryStr = isWorldwide
    ? companyDetail?.overview?.worldwide || 'worldwide'
    : [
        ...(remoteValue?.displayState || []).map((v) => {
          return `${v.value}`
        }),
        ...(remoteValue?.displayCountry || []).map((v) => {
          return `${v.value} `
        })
      ].join(', ')

  return countryStr
}

const JobDetail = ({ detail, config, langKey }: any) => {
  const langDic = useContext(languageContext) as any
  const { home } = langDic
  let jobLocation = useJobLocation(detail.remote_countries || [], detail.is_remote_worldwide)
  const job_languages = config.job_languages || []
  const language = (detail.languages || [])
    .map((v) => {
      const cur = job_languages.find((v2) => v2.id === v.id)
      return cur?.name || v.name
    })
    .join(',')

  const href = '/' + langKey + detail.job_url
  const handleAddJobView = (ev) => {
    ev.stopPropagation()
    ev.preventDefault()
    setJobViewCookiesTracker(setCookie, {
      source: viewJobSourceTracker.remoteLandingPage,
      reco_from: detail?.reco_from ? detail?.reco_from : null,
      query_id: detail?.query_id ? detail?.query_id : null
    })
    window.open(href, '_blank')
  }
  return (
    <div className={styles.jobDetailForRemoteHomePage} data-gtag-event='rl_active_job_detail'>
      <div className={styles.companyLogo}>
        <Image
          alt={detail?.job_title}
          src={detail?.company_logo}
          width={60}
          height={60}
          quality={0}
        ></Image>
      </div>

      <Link
        className={styles.contentWrapper}
        href={href}
        external={true}
        onClick={(ev) => handleAddJobView(ev)}
      >
        <div className={styles.jobTitle}>{detail?.job_title}</div>
        <div className={styles.salaryRoot}>
          <div className={styles.salary}>
            <ShowSalary jobs={detail} size={16} dictionary={langDic} />
          </div>
          <Link href={href} className={styles.chatAndApplyBtnLink}>
            <div className={styles.chatAndApplyBtn}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 18 18'
                fill='none'
              >
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M16.7175 5.57641C16.3469 4.52264 15.7826 3.61802 15.0407 2.88755C14.3052 2.16415 13.3997 1.61549 12.3486 1.25718C11.361 0.920628 10.2343 0.75 9.00015 0.75C7.76604 0.75 6.63636 0.926512 5.64319 1.27041C4.5953 1.63462 3.69186 2.18916 2.95639 2.91844C2.22093 3.64773 1.65403 4.55529 1.28336 5.60407C0.929453 6.60665 0.75 7.74897 0.75 9.00015C0.75 10.2651 0.931513 11.4186 1.28954 12.4262C1.66315 13.4779 2.23093 14.382 2.97552 15.1124C3.71098 15.8347 4.61678 16.3827 5.66555 16.7416C6.65196 17.0788 7.77398 17.25 9.00015 17.25C12.7528 17.25 15.1848 15.4593 15.1848 15.4593L16.4422 16.283C16.4422 16.283 17.25 12.8519 17.25 9.00074C17.25 7.73309 17.0708 6.58135 16.7175 5.57641ZM13.6901 13.5765C13.6295 13.5394 12.8766 13.0426 12.8766 13.0426C11.6411 13.7624 10.4675 14.074 9.00015 14.074C7.30976 14.074 6.03093 13.6621 5.19927 12.8452C4.35466 12.0173 3.92721 10.7229 3.92721 9.00015C3.92721 7.29387 4.35319 6.0071 5.19221 5.17367C6.02711 4.34583 7.30799 3.92632 9.00015 3.92632C10.7038 3.92721 11.9879 4.33907 12.8116 5.15131C13.6486 5.97503 14.0657 7.27181 14.1128 9.00015C14.1649 10.8544 13.891 12.5013 13.6901 13.5765ZM6.90276 6.90645C6.78709 6.90641 6.67253 6.92917 6.56565 6.97342C6.45877 7.01767 6.36166 7.08255 6.27986 7.16434C6.19807 7.24614 6.13319 7.34325 6.08894 7.45013C6.04469 7.55701 6.02193 7.67157 6.02197 7.78724V8.41268C6.02193 8.52838 6.04469 8.64294 6.08893 8.74984C6.13318 8.85674 6.19805 8.95387 6.27985 9.03569C6.36164 9.11751 6.45875 9.18241 6.56563 9.2267C6.67251 9.27098 6.78707 9.29377 6.90276 9.29377C7.13627 9.2933 7.36008 9.20032 7.52517 9.03517C7.69026 8.87003 7.78317 8.64619 7.78356 8.41268V7.78724C7.78356 7.55364 7.69076 7.32961 7.52558 7.16443C7.3604 6.99925 7.13637 6.90645 6.90276 6.90645ZM10.4742 7.16443C10.6393 6.99925 10.8634 6.90645 11.097 6.90645C11.2127 6.90641 11.3272 6.92917 11.4341 6.97342C11.541 7.01767 11.6381 7.08255 11.7199 7.16434C11.8017 7.24614 11.8666 7.34325 11.9108 7.45013C11.9551 7.55701 11.9778 7.67157 11.9778 7.78724V8.41268C11.9778 8.52838 11.9551 8.64294 11.9108 8.74984C11.8666 8.85674 11.8017 8.95387 11.7199 9.03569C11.6381 9.11751 11.541 9.18241 11.4341 9.2267C11.3272 9.27098 11.2127 9.29377 11.097 9.29377C10.8635 9.2933 10.6397 9.20032 10.4746 9.03517C10.3095 8.87003 10.2166 8.64619 10.2162 8.41268V7.78724C10.2162 7.55364 10.309 7.32961 10.4742 7.16443Z'
                  fill='white'
                />
              </svg>
              {home.jobCard.chatNow}
            </div>
          </Link>
        </div>
        <div className={styles.job_tags}>
          <Tags
            data={[
              jobLocation,
              language
              // xpLvlValue, degreeValue
            ].filter(Boolean)}
          />
        </div>
      </Link>
    </div>
  )
}

export default memo(JobDetail)
